import { AlertCircle, Loader2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { CaseEvidence, EvidenceResult } from '@/services/api/caseEvidence'
import { cn } from '@/utils/cn'
import { phoneCellWidthClass } from './cell-width'

export function CaseEvidenceView({
  state,
}: {
  state: { loading: boolean; error: boolean; evidence?: CaseEvidence }
}) {
  if (state.loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Verifying source evidence…
      </p>
    )
  }
  if (state.error || !state.evidence) {
    return (
      <p role="alert" className="flex items-center gap-2 text-sm text-destructive">
        <AlertCircle className="size-4 max-md:shrink-0" />
        Evidence is unavailable. The source may have been removed, changed, or become inaccessible.
      </p>
    )
  }
  return <EvidenceContent evidence={state.evidence} />
}

function EvidenceContent({ evidence }: { evidence: CaseEvidence }) {
  const result = evidence.result
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">
          {evidence.kind === 'rows'
            ? 'Original source rows'
            : 'Verified calculation / query result'}
        </p>
        {evidence.inputs.map(input => (
          <div key={input.table} className="text-xs text-muted-foreground">
            <p>
              {input.filename} · {input.table_id} · Interpretation: {input.interpretation.status}
            </p>
            <p className="break-all font-mono">
              Source version: {input.source_sha256} · Parser {input.parser_version}
            </p>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {result.total_rows} result rows · {result.count} shown from position {result.offset}.
        {result.truncated && ' This is a saved result page; the query scanned beyond this page.'}
        {' Source positions are zero-based and remain tied to this version.'}
      </p>
      <EvidenceRows result={result} />
      <ParsingSummary result={result} />
      <EvidenceCalculation evidence={evidence} />
    </div>
  )
}

function EvidenceCalculation({ evidence }: { evidence: CaseEvidence }) {
  const result = evidence.result
  return (
    <details className="rounded-md border border-border p-3 text-xs">
      <summary className="cursor-pointer font-medium">Inputs and calculation</summary>
      <p className="my-2 text-muted-foreground">
        Scope: {result.coverage.requested_scope}. Execution:{' '}
        {result.execution_complete ? 'complete' : 'incomplete'}. Counts describe source rows; they
        do not establish unique events.
      </p>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono">
        {JSON.stringify(
          {
            query: evidence.specification,
            interpretations: evidence.inputs.map(input => input.interpretation),
            engine: result.engine,
          },
          null,
          2
        )}
      </pre>
    </details>
  )
}

function EvidenceRows({ result }: { result: EvidenceResult }) {
  return (
    <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            {result.columns.map(column => (
              <TableHead key={column}>
                {column === '__source_row' ? 'Source row' : column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {result.rows.map((row, index) => (
            <TableRow key={`${result.offset}-${index}`}>
              {result.columns.map(column => {
                const text = displayCell(row[column])
                return (
                  <TableCell
                    key={column}
                    className={cn(
                      'min-w-32 max-w-lg whitespace-pre-wrap break-words align-top font-mono text-xs',
                      phoneCellWidthClass(text)
                    )}
                  >
                    {text}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {result.rows.length === 0 && (
        <p className="p-3 text-sm text-muted-foreground">No matching result rows.</p>
      )}
    </div>
  )
}

function displayCell(value: unknown): string {
  if (value === null || value === undefined) return '—'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

function ParsingSummary({ result }: { result: EvidenceResult }) {
  if (!result.parsing.length) return null
  return (
    <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
      <p>
        Conversions are explicit query interpretations. Unknown units and timezones remain unknown.
      </p>
      {result.parsing.map(item => (
        <p key={item.alias}>
          {item.column} → {item.alias}: {item.invalid_rows} invalid values, {item.null_rows} total
          unparsed or empty values out of {item.input_rows} input rows.
        </p>
      ))}
    </div>
  )
}
